import * as i0 from '@angular/core';
import { InjectionToken, forwardRef, EventEmitter, Component, ViewEncapsulation, ChangeDetectionStrategy, HostBinding, Input, Output, HostListener, Directive, ContentChildren, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { __decorate } from 'tslib';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { SlideInOutState, slideInOutAnimation } from '@geneplanet/ngx-ui/src/lib/shared';
import { merge } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

const GPU_COLLAPSE = new InjectionToken('GpuCollapseComponent');
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parentProvider = {
    provide: GPU_COLLAPSE,
    useExisting: forwardRef(() => GpuCollapseComponent),
    multi: true,
};
class GpuCollapseComponent {
    constructor(cdRef) {
        this.cdRef = cdRef;
        this.collapseClass = true;
        /**
         * If content is collapsed
         */
        this.isCollapsed = true;
        this.showArrow = true;
        this.changed = new EventEmitter();
    }
    onClick() {
        this.toggleOpen();
    }
    ngOnChanges(changes) {
        if (changes.isCollapsed?.currentValue !== changes.isCollapsed?.previousValue) {
            this.changed.emit(changes.isCollapsed?.currentValue);
        }
    }
    toggleOpen() {
        this.isCollapsed = !this.isCollapsed;
        this.changed.emit(this.isCollapsed);
        this.cdRef.markForCheck();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCollapseComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: GpuCollapseComponent, selector: "gpu-collapse", inputs: { isCollapsed: "isCollapsed", showArrow: "showArrow" }, outputs: { changed: "changed" }, host: { listeners: { "click": "onClick()" }, properties: { "class.gpu-collapse": "this.collapseClass", "class.gpu-collapse-collapsed": "this.isCollapsed" } }, providers: [parentProvider], usesOnChanges: true, ngImport: i0, template: "<div class=\"gpu-header-container\">\n  <ng-content select=\"gpu-collapse-header\"></ng-content>\n\n  <i *ngIf=\"showArrow\" class=\"gpu-header-icon gpicon-down\" [class.opened]=\"!isCollapsed\"></i>\n</div>\n\n<ng-content select=\"gpu-collapse-content\"></ng-content>\n", styles: [".gpu-collapse{display:inline-block;cursor:pointer;background:#fff;border-radius:12px;padding:.75rem .75rem .75rem 1rem}@media (min-width: 576px){.gpu-collapse{padding:1rem 1rem 1rem 1.5rem}}.gpu-collapse .gpu-header-icon{height:2rem;width:2rem;font-size:2rem!important;transition:transform .2s linear;color:#c7c7d2;margin-top:-.25rem!important;margin-bottom:-.25rem!important}@media (min-width: 576px){.gpu-collapse .gpu-header-icon{margin-top:0!important;margin-bottom:0!important}}.gpu-collapse .gpu-header-icon.opened{transform:rotate(-180deg)}.gpu-collapse .gpu-header-container{display:flex;flex-direction:row;justify-content:space-between;align-items:center}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCollapseComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gpu-collapse', providers: [parentProvider], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"gpu-header-container\">\n  <ng-content select=\"gpu-collapse-header\"></ng-content>\n\n  <i *ngIf=\"showArrow\" class=\"gpu-header-icon gpicon-down\" [class.opened]=\"!isCollapsed\"></i>\n</div>\n\n<ng-content select=\"gpu-collapse-content\"></ng-content>\n", styles: [".gpu-collapse{display:inline-block;cursor:pointer;background:#fff;border-radius:12px;padding:.75rem .75rem .75rem 1rem}@media (min-width: 576px){.gpu-collapse{padding:1rem 1rem 1rem 1.5rem}}.gpu-collapse .gpu-header-icon{height:2rem;width:2rem;font-size:2rem!important;transition:transform .2s linear;color:#c7c7d2;margin-top:-.25rem!important;margin-bottom:-.25rem!important}@media (min-width: 576px){.gpu-collapse .gpu-header-icon{margin-top:0!important;margin-bottom:0!important}}.gpu-collapse .gpu-header-icon.opened{transform:rotate(-180deg)}.gpu-collapse .gpu-header-container{display:flex;flex-direction:row;justify-content:space-between;align-items:center}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { collapseClass: [{
                type: HostBinding,
                args: ['class.gpu-collapse']
            }], isCollapsed: [{
                type: HostBinding,
                args: ['class.gpu-collapse-collapsed']
            }, {
                type: Input
            }], showArrow: [{
                type: Input
            }], changed: [{
                type: Output
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class CollapseHeaderComponent {
    constructor() {
        this.collapseContentClass = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CollapseHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: CollapseHeaderComponent, selector: "gpu-collapse-header", host: { properties: { "class.gpu-collapse-header": "this.collapseContentClass" } }, ngImport: i0, template: `<ng-content></ng-content>`, isInline: true, styles: [".gpu-collapse-header{width:100%}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CollapseHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gpu-collapse-header', template: `<ng-content></ng-content>`, encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, styles: [".gpu-collapse-header{width:100%}\n"] }]
        }], propDecorators: { collapseContentClass: [{
                type: HostBinding,
                args: ['class.gpu-collapse-header']
            }] } });

let CollapseContentComponent = class CollapseContentComponent {
    get getToggleDrawer() {
        return this.parent.isCollapsed ? SlideInOutState.IN : SlideInOutState.OUT;
    }
    constructor(parent, cdRef) {
        this.parent = parent;
        this.cdRef = cdRef;
        this.collapseContentClass = true;
        /** If click on content can toggle state. If enabled it will propagate click. */
        this.canToggle = false;
    }
    onClick(event) {
        if (!this.canToggle) {
            event.stopImmediatePropagation();
        }
    }
    ngOnInit() {
        this.parent.changed.pipe(untilDestroyed(this)).subscribe(() => {
            this.cdRef.markForCheck();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CollapseContentComponent, deps: [{ token: GpuCollapseComponent }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: CollapseContentComponent, selector: "gpu-collapse-content", inputs: { canToggle: "canToggle" }, host: { listeners: { "click": "onClick($event)" }, properties: { "class.gpu-collapse-content": "this.collapseContentClass", "@slideInOut": "this.getToggleDrawer" } }, ngImport: i0, template: "<div>\n  <ng-content></ng-content>\n</div>\n", styles: [".gpu-collapse-content{display:block;overflow:hidden}.gpu-collapse-content>div{padding-top:.75rem;padding-bottom:.75rem}@media (min-width: 576px){.gpu-collapse-content>div{padding-bottom:.5rem}}\n"], animations: [slideInOutAnimation], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
CollapseContentComponent = __decorate([
    UntilDestroy()
], CollapseContentComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: CollapseContentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gpu-collapse-content', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, animations: [slideInOutAnimation], template: "<div>\n  <ng-content></ng-content>\n</div>\n", styles: [".gpu-collapse-content{display:block;overflow:hidden}.gpu-collapse-content>div{padding-top:.75rem;padding-bottom:.75rem}@media (min-width: 576px){.gpu-collapse-content>div{padding-bottom:.5rem}}\n"] }]
        }], ctorParameters: () => [{ type: GpuCollapseComponent }, { type: i0.ChangeDetectorRef }], propDecorators: { collapseContentClass: [{
                type: HostBinding,
                args: ['class.gpu-collapse-content']
            }], getToggleDrawer: [{
                type: HostBinding,
                args: ['@slideInOut']
            }], canToggle: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

let GpuAccordionDirective = class GpuAccordionDirective {
    constructor() {
        this.onlyOneExpanded = true;
    }
    ngAfterViewInit() {
        merge(...this.collapses.map((collapse) => collapse.changed.pipe(
        // Ignore collapsing
        filter((isCollapsed) => !isCollapsed), 
        // Remember which element it is, so we don't hide it later
        tap(() => (this.lastElement = collapse)))))
            .pipe(untilDestroyed(this))
            .subscribe(() => {
            if (!this.onlyOneExpanded) {
                return;
            }
            this.hideAllExceptLast();
        });
    }
    hideAllExceptLast() {
        this.collapses.forEach((collapse) => {
            if (!collapse.isCollapsed && this.lastElement !== collapse) {
                collapse.toggleOpen();
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuAccordionDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.1.3", type: GpuAccordionDirective, selector: "[gpuAccordion]", inputs: { onlyOneExpanded: "onlyOneExpanded" }, queries: [{ propertyName: "collapses", predicate: GpuCollapseComponent, descendants: true }], ngImport: i0 }); }
};
GpuAccordionDirective = __decorate([
    UntilDestroy()
], GpuAccordionDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuAccordionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[gpuAccordion]',
                }]
        }], ctorParameters: () => [], propDecorators: { collapses: [{
                type: ContentChildren,
                args: [GpuCollapseComponent, { descendants: true }]
            }], onlyOneExpanded: [{
                type: Input
            }] } });

class GpuCollapseModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCollapseModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: GpuCollapseModule, declarations: [GpuCollapseComponent, CollapseHeaderComponent, CollapseContentComponent, GpuAccordionDirective], imports: [CommonModule], exports: [GpuCollapseComponent, CollapseHeaderComponent, CollapseContentComponent, GpuAccordionDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCollapseModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCollapseModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [GpuCollapseComponent, CollapseHeaderComponent, CollapseContentComponent, GpuAccordionDirective],
                    imports: [CommonModule],
                    exports: [GpuCollapseComponent, CollapseHeaderComponent, CollapseContentComponent, GpuAccordionDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CollapseContentComponent, CollapseHeaderComponent, GPU_COLLAPSE, GpuAccordionDirective, GpuCollapseComponent, GpuCollapseModule, parentProvider };
